import { Container, Group, SimpleGrid, Stack, Text, createPolymorphicComponent } from '@mantine/core';
import type { TextProps } from '@mantine/core';
import { Link, useParams } from 'react-router';
import { styled, useTheme } from '@epihub/web/src/_ctx/theme';
import { assets } from '../assets';
import { useSfyr } from '../_ctx/sfyr';

const FooterRoot = styled.footer`
  padding: 48px 0;
  margin-top: auto;
`;

const FooterDivider = styled.div`
  padding-top: 24px;
`;

const FooterLink = styled(Link)`
  text-decoration: none !important;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.7;
  }
`;

const FooterAnchor = styled.a`
  text-decoration: none !important;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.7;
  }
`;

const FooterTagline = createPolymorphicComponent<'p', TextProps>(styled(Text)`
  line-height: 1.6;
`);

export const Footer = () => {
  const theme = useTheme();
  const sfyr = useSfyr();
  const { lang } = useParams<{ lang: string }>();
  const langPrefix = lang ? `/${lang}` : '';

  return (
    <FooterRoot
      style={{
        borderTop: `1px solid ${theme.v0.col.backgroundSecondary.hex}`,
        backgroundColor: theme.v0.col.backgroundSecondary.hex,
      }}
    >
      <Container size='lg'>
        <Stack gap='lg'>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing='xl'>
            <div>
              <Group gap='xs' mb='md'>
                <assets.LogoRect.Component height={20} />
              </Group>
              <FooterTagline size='sm' c={theme.v0.col.textDimmed.hex} maw={300}>
                {sfyr('landing.{lang}.footer.tagline', {
                  defaults: {
                    'landing.{lang=en}.footer.tagline': 'Software studio based in Brescia, Italy.',
                  },
                })}
              </FooterTagline>
            </div>
            <div>
              <Text fw={600} mb='md'>
                {sfyr('landing.{lang}.footer.nav_heading', {
                  defaults: { 'landing.{lang=en}.footer.nav_heading': 'Navigation' },
                })}
              </Text>
              <Stack gap='xs'>
                <FooterLink to={`${langPrefix}/`}>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    {sfyr('landing.{lang}.nav.home', { defaults: { 'landing.{lang=en}.nav.home': 'Home' } })}
                  </Text>
                </FooterLink>
                <FooterLink to={`${langPrefix}/#about`}>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    {sfyr('landing.{lang}.nav.about', { defaults: { 'landing.{lang=en}.nav.about': 'About' } })}
                  </Text>
                </FooterLink>
                <FooterLink to={`${langPrefix}/blog`}>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    {sfyr('landing.{lang}.nav.blog', { defaults: { 'landing.{lang=en}.nav.blog': 'Blog' } })}
                  </Text>
                </FooterLink>
              </Stack>
            </div>
            <div>
              <Text fw={600} mb='md'>
                {sfyr('landing.{lang}.footer.legal_heading', {
                  defaults: { 'landing.{lang=en}.footer.legal_heading': 'Legal' },
                })}
              </Text>
              <Stack gap='xs'>
                <FooterLink to={`${langPrefix}/privacy`}>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    {sfyr('landing.{lang}.footer.privacy', {
                      defaults: { 'landing.{lang=en}.footer.privacy': 'Privacy Policy' },
                    })}
                  </Text>
                </FooterLink>
                <FooterLink to={`${langPrefix}/terms`}>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    {sfyr('landing.{lang}.footer.terms', {
                      defaults: { 'landing.{lang=en}.footer.terms': 'Terms of Service' },
                    })}
                  </Text>
                </FooterLink>
              </Stack>
            </div>
            <div>
              <Text fw={600} mb='md'>
                {sfyr('landing.{lang}.footer.contact_heading', {
                  defaults: { 'landing.{lang=en}.footer.contact_heading': 'Contact' },
                })}
              </Text>
              <Stack gap='xs'>
                <FooterAnchor href='mailto:info@epilogo.co'>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    info@epilogo.co
                  </Text>
                </FooterAnchor>
                <FooterAnchor href='mailto:info@epilogo.co?subject=Quote%20request'>
                  <Text size='sm' c={theme.v0.col.primary.hex} style={{ cursor: 'pointer' }}>
                    {sfyr('landing.{lang}.footer.request_quote', {
                      defaults: { 'landing.{lang=en}.footer.request_quote': 'Request quote' },
                    })}
                  </Text>
                </FooterAnchor>
              </Stack>
            </div>
          </SimpleGrid>
          <FooterDivider style={{ borderTop: `1px solid ${theme.v0.col.backgroundTertiary.hex}` }}>
            <Text size='sm' c={theme.v0.col.textDimmed.hex} ta='center'>
              {new Date().getFullYear()} {sfyr('landing.{lang}.footer.copyright', {
                defaults: { 'landing.{lang=en}.footer.copyright': 'Epilogo SRL. All rights reserved.' },
              })}
            </Text>
          </FooterDivider>
        </Stack>
      </Container>
    </FooterRoot>
  );
};
